import { Component, AfterViewInit, ViewChild, ChangeDetectorRef, AfterContentChecked, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import { ShortcutInput } from 'ng-keyboard-shortcuts';
import { NavigationStart, Router } from '@angular/router';
import { PagePermissions } from 'src/app/shared/models/permissions';
import { RolesEnum } from 'src/app/shared/models/roles';
import { AppService } from '../../services/app.service';
import { HasPermissionsPipe } from '@shared/pipes/has-permissions.pipe';
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';
import { LandingModulesNames, MenuItem, ModulePage, ModuleSideNavTabs, childrensMap } from '@shared/models/modules-landing.model';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { IsInRolePipe } from '@shared/pipes/is-in-role.pipe';
import { environment } from 'src/environments/environment';
import { HelpersService, SystemSetting, SystemSettingsEnum } from '@shared';
import { Subscription } from 'rxjs';
import { PagesMenuService } from '@shared/services/pages-menu.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { PageMenuDateService } from '@shared/services/page-menu-date.service';
import { Location } from '@angular/common';
import { SystemSettingsService } from '@core/services/system-settings.service';
import { GlobalSettings, InvoicingTransactions, ModulesActivation } from 'src/app/system/models/system-settings';
import { OnlineOfflineService } from '@core/services/online-offine.service';
import { ModuleActivationService } from '@core/services/module-activation.service';
import { EmbedService } from '@core/services/embed.service';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  providers: [HasPermissionsPipe, HasPermissionPipe, IsInRolePipe],
  styleUrls: ['./nav-menu.component.scss'],
  animations: [
    trigger('heightAnimation', [
      state('*', style({
        height: '*',
        overflow: 'hidden'
      })),
      transition(':enter', [
        style({
          height: '0',
          overflow: 'hidden'
        }),
        animate('300ms cubic-bezier(0.68, -0.55, 0.27, 1.55)')
      ]),
      transition(':leave', [
        animate('100ms cubic-bezier(0.68, -0.55, 0.27, 1.55)', style({
          height: '0',
          overflow: 'hidden'
        }))
      ])
    ])
  ]
})
export class NavMenuComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {

  modulesActivationSettings: ModulesActivation = new ModulesActivation();
  isPointOfSaleV3Enabled: boolean = false;
  shortcuts: ShortcutInput[] = [];
  bottomSheet!: MatBottomSheetRef;
  selectedMenuIndex: number = -1;
  menuItems: any[] = [];
  moduleSideNavTabs = ModuleSideNavTabs;
  version: string = '';

  /**
   * Running inside the Edara Portal, whose header already provides the theme toggle — so this
   * menu drops its own copy, and the build stamp with it.
   */
  isEmbedded = false;

  public get pagePermissions(): typeof PagePermissions {
    return PagePermissions;
  }

  public get roles(): typeof RolesEnum {
    return RolesEnum;
  }

  private get _sideNavMenu(): any[] {
    return [
      {
        label: 'sideNav.home',
        icon: 'home',
        routerLink: '/',
        link: '/home',
        primaryMenu: true,
        level2Location: 'below',
        module: LandingModulesNames.noModule,
        hasStarredPages: false,
        details: []
      },
      {
        label: 'sideNav.purchasing',
        icon: 'shopping_cart',
        link: '/purchasing/',
        routerLink: "/purchasing/landing",
        id: 'purchasing',
        primaryMenu: true,
        level2Location: 'below',
        module: LandingModulesNames.purchasing,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: !this.modulesActivationSettings.purchasing,
        hasStarredPages: false,
        details: [
          { id: "purchasing_purchaseRequests", label: 'sideNav.purchaseRequests', description: "pages.purchaseRequestsDesc", routerLink: '/purchasing/purchase-requests', permissions: this.hasPermissions.transform(this.pagePermissions.PurchaseRequests), isStarred: false, trackNavigation: true },
          { id: "purchasing_purchaseOrders", label: 'sideNav.purchaseOrders', description: "pages.purchaseOrdersDesc", routerLink: '/purchasing/purchase-orders', permissions: this.hasPermissions.transform(this.pagePermissions.PurchaseOrders), isStarred: false, trackNavigation: true },
          { id: "purchasing_products", label: 'sideNav.products', description: "pages.purchasingProductsDesc", routerLink: '/purchasing/products', permissions: this.hasPermissions.transform(this.pagePermissions.Products), isStarred: false },
          { id: "purchasing_suppliers", label: 'sideNav.suppliers', description: "pages.purchasingSuppliersDesc", routerLink: '/purchasing/suppliers', permissions: this.hasPermissions.transform(this.pagePermissions.Suppliers), isStarred: false },
          { id: "purchasing_locations", label: 'sideNav.locations', description: "pages.purchasingLocationsDesc", routerLink: '/purchasing/locations', permissions: this.hasPermissions.transform(this.pagePermissions.Locations), isStarred: false }
        ]
      },
      {
        label: 'sideNav.pos',
        icon: 'trending_up',
        link: '/pos/',
        routerLink: "/pos/landing",
        id: 'pos',
        primaryMenu: true,
        level2Location: 'below side-nav__sub--pos',
        module: LandingModulesNames.pos,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: !this.modulesActivationSettings.pointOfSale,
        hasStarredPages: false,
        details: [
          { id: "pos_customers", checkForModuleVersion: true, isV3: false, label: 'sideNav.customers', description: "pages.posCustomersDesc", routerLink: '/pos/customers', permissions: this.hasPermissions.transform(this.pagePermissions.Customers), isStarred: false },
          { id: "pos_customers", checkForModuleVersion: true, isV3: true, label: 'sideNav.customers', description: "pages.posCustomersDesc", routerLink: '/pos/customers-v3', permissions: this.hasPermissions.transform(this.pagePermissions.Customers), isStarred: false },
          { id: "pos_salesOrders", label: 'sideNav.salesOrders', description: "pages.posOrdersDesc", routerLink: '/pos/sales-orders', checkForModuleVersion: true, isV3: false, permissions: this.hasPermissions.transform(this.pagePermissions.SalesOrders), isStarred: false },
          { id: "pos_salesOrders", label: 'sideNav.salesOrders', description: "pages.posOrdersDesc", routerLink: '/pos/sales-orders-v3', checkForModuleVersion: true, isV3: true, permissions: this.hasPermissions.transform(this.pagePermissions.SalesOrders), isStarred: false },
          { id: "pos_cashInSlashOut", label: 'sideNav.cashInSlashOut', description: "pages.posCashInOutDesc", routerLink: '/pos/cash-management', permissions: this.hasPermissions.transform(this.pagePermissions.CashRegisters), isStarred: false },
          { id: "pos_registerClosures", label: 'sideNav.registerClosures', description: "pages.posRegisterClosuresDesc", routerLink: '/pos/register-closures', permissions: this.isInRole.transform(this.roles.SystemAdministrator) && this.hasPermissions.transform(this.pagePermissions.CashRegisters), isStarred: false },
          { id: "pos_products", label: 'sideNav.products', description: "pages.posProductsDesc", routerLink: '/pos/products', checkForModuleVersion: true, isV3: true, permissions: this.hasPermissions.transform(this.pagePermissions.Products), isStarred: false },
          { id: "pos_serviceItems", label: 'sideNav.serviceItems', description: "pages.posServiceItemsDesc", routerLink: '/pos/service-items', checkForModuleVersion: true, isV3: false, permissions: this.hasPermissions.transform(this.pagePermissions.ServiceItems), isStarred: false },
          { id: "pos_promotions", label: 'sideNav.promotions', description: "pages.posPromotionsDesc", routerLink: '/pos/promotions', permissions: this.hasPermissions.transform(this.pagePermissions.Promotions), isStarred: false },
          { id: "pos_cashRegisters", label: 'sideNav.cashRegisters', description: "pages.posCashRegisterDesc", routerLink: '/pos/cash-registers', permissions: this.isInRole.transform(this.roles.SystemAdministrator) && this.hasPermissions.transform(this.pagePermissions.CashRegisters), isStarred: false },
          { id: "pos_deliveryZones", label: 'sideNav.deliveryZones', description: "pages.posDeliveryZonesDesc", routerLink: '/pos/delivery-zones', checkForModuleVersion: true, isV3: true, permissions: this.isInRole.transform(this.roles.SystemAdministrator) && this.hasPermissions.transform(this.pagePermissions.DeliveryZones), isStarred: false }
        ]
      },
      {
        label: 'sideNav.fixedAssets',
        icon: 'business',
        link: '/fixed-assets/',
        routerLink: "/fixed-assets/landing",
        id: 'fixedAssets',
        primaryMenu: false,
        level2Location: 'below',
        module: LandingModulesNames.fixedAssets,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: !this.modulesActivationSettings.fixedAssets,
        hasStarredPages: false,
        details: [
          { id: "fixedAssets_assets", label: 'sideNav.assets', description: "pages.fixedAssetsAssetsDesc", routerLink: '/fixed-assets/onboard', permissions: this.hasPermissions.transform(this.pagePermissions.FixedAssets), isStarred: false, trackNavigation: true },
          { id: "fixedAssets_depreciation", label: 'sideNav.depreciation', description: "pages.fixedAssetsDepreciationDesc", routerLink: '/fixed-assets/depreciation', permissions: this.hasPermissions.transform(this.pagePermissions.FixedAssetsDepreciation), isStarred: false }
        ]
      },
      {
        label: 'sideNav.manufacturing',
        icon: 'build',
        link: '/manufacturing/',
        routerLink: "/manufacturing/landing",
        id: 'Manufacturing',
        primaryMenu: false,
        level2Location: 'below',
        module: LandingModulesNames.manufacturing,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: !this.modulesActivationSettings.manufacturing,
        hasStarredPages: false,
        details: [
          { id: "Manufacturing_billOfMaterials", label: 'sideNav.bom', description: "pages.manufacturingBillOfMaterialsDesc", routerLink: '/manufacturing/bill-of-materials', permissions: this.hasPermissions.transform(this.pagePermissions.BillOfMaterials), isStarred: false, trackNavigation: true },
          { id: "fixedAsManufacturing_productionOrdersepreciation", label: 'sideNav.production', description: "pages.manufacturingProductionOrdersDesc", routerLink: '/manufacturing/production-orders', permissions: this.hasPermissions.transform(this.pagePermissions.ProductionOrders), isStarred: false, trackNavigation: true },
          { id: "Manufacturing_materialRequirements", label: 'sideNav.materialRequirements', description: "pages.manufacturingMaterialRequirementsDesc", routerLink: '/manufacturing/material-requirements', permissions: this.hasPermissions.transform(this.pagePermissions.ProductionOrders), isStarred: false, trackNavigation: true },
          { id: "Manufacturing_productionPlans", label: 'sideNav.productionPlanning', description: "pages.manufacturingProductionPlanningDesc", routerLink: '/manufacturing/production-planning', permissions: this.hasPermissions.transform(this.pagePermissions.ProductionPlans), isStarred: false }
        ]
      },
      {
        label: 'sideNav.invoicing',
        icon: 'receipt_long',
        link: '/invoicing/',
        routerLink: "/invoicing/landing",
        id: 'Invoicing',
        primaryMenu: false,
        level2Location: 'below',
        module: LandingModulesNames.invoicing,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: !this.modulesActivationSettings.invoicing,
        hasStarredPages: false,
        details: [
          { id: "Invoicing_purchaseInvoice", label: 'sideNav.purchaseInvoice', description: "pages.invoicingPurchaseInvoicesDesc", routerLink: '/invoicing/purchase-invoices', permissions: this.hasPermissions.transform(this.pagePermissions.PurchaseInvoices), transactionType: InvoicingTransactions.PurchaseInvoices, isStarred: false, trackNavigation: true },
          { id: "Invoicing_debitNote", label: 'sideNav.debitNote', description: "pages.invoicingDebitNotesDesc", routerLink: '/invoicing/debit-notes', permissions: this.hasPermissions.transform(this.pagePermissions.DebitNotes), transactionType: InvoicingTransactions.All, isStarred: false },
          { id: "Invoicing_salesInvoice", label: 'sideNav.salesInvoice', description: "pages.invoicingSalesInvoicesDesc", routerLink: '/invoicing/sales-invoices', permissions: this.hasPermissions.transform(this.pagePermissions.SalesInvoices), transactionType: InvoicingTransactions.SalesInvoices, isStarred: false },
          { id: "Invoicing_creditNote", label: 'sideNav.creditNote', description: "pages.invoicingCreditNotesDesc", routerLink: '/invoicing/credit-notes', permissions: this.hasPermissions.transform(this.pagePermissions.CreditNotes), transactionType: InvoicingTransactions.SalesInvoices, isStarred: false },
          { id: "Invoicing_payments", label: 'sideNav.payments', description: "pages.invoicingPaymentsDesc", routerLink: '/invoicing/payments', permissions: this.hasPermissions.transform(this.pagePermissions.Payments), transactionType: InvoicingTransactions.All, isStarred: false }
        ]
      },
      {
        label: 'sideNav.analytics',
        icon: 'monitoring',
        link: '/analytics',
        routerLink: '/analytics',
        id: 'dataAnalytics',
        primaryMenu: false,
        level2Location: 'below',
        module: LandingModulesNames.noModule,
        isHidden: !this.modulesActivationSettings.analytics,
        hasStarredPages: false,
        details: []
      },
      {
        label: 'sideNav.data',
        icon: 'cloud',
        link: '/data/',
        routerLink: "/data/landing",
        id: "Data",
        primaryMenu: false,
        level2Location: 'below',
        module: LandingModulesNames.dataModule,
        activeTab: ModuleSideNavTabs.starred,
        hasStarredPages: false,
        details: [
          { id: "offline_sales_orders", label: 'sideNav.offlineSalesOrders', description: "pages.dataOfflineSalesOrdersDesc", routerLink: '/data/offline-sales-orders', permissions: this.isInRole.transform(this.roles.SystemAdministrator) || this.isInRole.transform(this.roles.OrderManager) && this.hasPermissions.transform(this.pagePermissions.SalesOrders), isStarred: false },
          { id: "potential_fraud_detector", label: 'sideNav.potentialFraudDetector', description: "pages.dataPotentialFraudDetectorDesc", routerLink: '/data/potential-fraud-detector', permissions: this.isInRole.transform(this.roles.SystemAdministrator), isStarred: false },
          {
            id: "local_data", label: 'sideNav.localData', description: "pages.localDataDesc", routerLink: '/data/local-data',
            permissions: this.hasPermissions.transform(PagePermissions.LocalData),
            isStarred: false
          }
        ]
      },
      {
        label: 'sideNav.system',
        icon: 'settings',
        link: '/system/',
        routerLink: "/system/landing",
        id: 'System',
        primaryMenu: true,
        level2Location: 'below',
        divider: true,
        module: LandingModulesNames.systemModule,
        activeTab: ModuleSideNavTabs.starred,
        hasStarredPages: false,
        details: [
          { id: "system_settings", label: 'sideNav.settings', description: "pages.systemSettingsDesc", routerLink: '/system/settings', permissions: this.isInRole.transform(this.roles.SystemAdministrator) && this.hasPermissions.transform(this.pagePermissions.SystemSettings), isStarred: false },
          { id: "user_management", label: 'sideNav.userManagement', description: "pages.userManagementDesc", routerLink: '/system/user-management', permissions: this.isInRole.transform(this.roles.SystemAdministrator), isStarred: false },
          { id: "system_integrations", label: 'sideNav.extensionsStore', description: "pages.systemIntegrationDesc", routerLink: '/system/extensions-store', permissions: this.isInRole.transform(this.roles.SystemAdministrator), isStarred: false },
          { id: "known_devices", label: 'sideNav.knownDevices', description: "pages.knownDevicesDesc", routerLink: '/system/known-devices', permissions: this.isInRole.transform(this.roles.SystemAdministrator), isStarred: false }
        ]
      },
      {
        label: 'sideNav.samples',
        icon: 'palette',
        link: '/samples/',
        primaryMenu: false,
        level2Location: 'middle',
        divider: true,
        module: LandingModulesNames.samples,
        activeTab: ModuleSideNavTabs.starred,
        isHidden: environment.name !== 'dev',
        hasStarredPages: false,
        details: [
          { id: "", label: 'Buttons', routerLink: '/samples/buttons', permissions: true, isStarred: false },
          { id: "", label: 'Typo', routerLink: '/samples/typo', permissions: true, isStarred: false },
          { id: "", label: 'Elevations', routerLink: '/samples/elevations', permissions: true, isStarred: false },
          { id: "", label: 'Forms', routerLink: '/samples/forms', permissions: true, isStarred: false },
          { id: "", label: 'Dialogs', routerLink: '/samples/dialogs', permissions: true, isStarred: false },
          { id: "", label: 'Bottom sheet', routerLink: '/samples/bottom-sheet', permissions: true, isStarred: false },
          { id: "", label: 'Cards', routerLink: '/samples/cards', permissions: true, isStarred: false },
          { id: "", label: 'Menu', routerLink: '/samples/menu', permissions: true, isStarred: false },
          { id: "", label: 'Sliders', routerLink: '/samples/sliders', permissions: true, isStarred: false },
          { id: "", label: 'Segmented buttons', routerLink: '/samples/segmented-buttons', permissions: true, isStarred: false },
          { id: "", label: 'Fab', routerLink: '/samples/fab', permissions: true, isStarred: false },
          { id: "", label: 'Snackbar', routerLink: '/samples/snackbar', permissions: true, isStarred: false },
          { id: "", label: 'Progress', routerLink: '/samples/progress', permissions: true, isStarred: false },
          { id: "", label: 'Symbols', routerLink: '/samples/symbols', permissions: true, isStarred: false },
          { id: "", label: 'Divider', routerLink: '/samples/divider', permissions: true, isStarred: false },
          { id: "", label: 'Badges', routerLink: '/samples/badges', permissions: true, isStarred: false },
          { id: "", label: 'Date picker', routerLink: '/samples/date-picker', permissions: true, isStarred: false },
          { id: "", label: 'Lists', routerLink: '/samples/lists', permissions: true, isStarred: false },
          { id: "", label: 'Navigation drawer', routerLink: '/samples/navigation-drawer', permissions: true, isStarred: false },
          { id: "", label: 'Navigation rails', routerLink: '/samples/navigation-rails', permissions: true, isStarred: false }
        ]
      }
    ];
  }

  private modulesActivationChangeSubscription?: Subscription;
  private layoutChangeSubscription?: Subscription;

  @ViewChild('moreMenuDialog', { static: false }) moreMenuDialog!: TemplateRef<any>;

  constructor(
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    public helpersService: HelpersService,
    public appService: AppService,
    private hasPermissions: HasPermissionsPipe,
    private isInRole: IsInRolePipe,
    private _bottomSheet: MatBottomSheet,
    public menuService: PagesMenuService,
    private location: Location,
    private pageMenuDateService: PageMenuDateService,
    private moduleActivationService: ModuleActivationService,
    private systemSettingService: SystemSettingsService,
    private onlineOfflineService: OnlineOfflineService,
    private embedService: EmbedService
  ) {
    this.isEmbedded = this.embedService.isEmbedded;
  }


  ngOnInit(): void {
    this.version = environment.version;
    this.loadPonitOfSaleVersion();
    this.loadModulesActivationSettings();

    this.menuService.starredPages$.subscribe((res) => {
      this.menuItemsMapper();
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.updateLatestOpendByRouterLink(event.url);
      }
    });
  }

  ngAfterViewInit() {
    this.renderNavMenu();
    this.initShortcuts();

    this.modulesActivationChangeSubscription = this.systemSettingService.modulesActivationSettingsChanged$.subscribe({
      next: res => {
        this.modulesActivationSettings = res;
        this.loadPonitOfSaleVersion();
        this.renderNavMenu();
      }
    });
  }

  ngAfterContentChecked() {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.modulesActivationChangeSubscription?.unsubscribe();
    this.layoutChangeSubscription?.unsubscribe();
  }

  initShortcuts() {
    this.shortcuts.push(
      {
        key: ['g h'],
        label: 'Navigation',
        description: 'Go to Home',
        command: () => this.router.navigate(['/']),
        preventDefault: true
      },
      {
        key: ['n v', 'g v'],
        label: 'Navigation',
        description: 'Go to new Service Item',
        command: () => this.router.navigate(['/pos/service-item/new']),
        preventDefault: true
      },
      {
        key: ['n s', 'g s'],
        label: 'Navigation',
        description: 'Go to new Sales Order',
        command: () => this.goToNewSalesOrder(),
        preventDefault: true
      }
    );
  }

  isActiveRoute(route: string): boolean {
    if (route === '/home' && this.router.url === '/')
      return true
    else
      return this.router.url.includes(route);
  }

  changeTheme() {
    if (this.appService.state.theme === 'light')
      this.appService.changeTheme('dark');
    else
      this.appService.changeTheme('light');
  }

  openMoreMenu() {
    this.selectedMenuIndex = -1;
    this.bottomSheet = this._bottomSheet.open(this.moreMenuDialog, {
      panelClass: 'nav-bottom-sheet'
    });
  }

  routeClick() {
    setTimeout(() => {
      this.menuService.navMenuState.selectedMenuIndex = -1;
    }, 100);
    if (this.bottomSheet)
      this.bottomSheet.dismiss()
  }

  private renderNavMenu() {
    setTimeout(() => {
      this.menuService.menu_items = this._sideNavMenu.filter(item => !item.isHidden && (!item.details.length || !!item.details.find((subItem: any) => subItem.permissions)));

      // Filter invoicing items based on invoicingTransactions
      if (this.modulesActivationSettings.invoicing && this.modulesActivationSettings.moduleTransactions?.invoicingTransactions) {
        const invoicingItems = this.menuService.menu_items.find(item => item.id === 'Invoicing');
        if (!invoicingItems) {
          return;
        }
        invoicingItems.details = invoicingItems.details.filter((subItem: any) => {
          if (this.modulesActivationSettings.moduleTransactions.invoicingTransactions === InvoicingTransactions.All) {
            return true;
          } else {
            return subItem.transactionType === InvoicingTransactions.All ||
              subItem.transactionType === this.modulesActivationSettings.moduleTransactions.invoicingTransactions;
          }
        });
      }
    });
  }

  private async loadPonitOfSaleVersion() {
    const isPointOfSaleV3Enabled = await this.moduleActivationService.isPointOfSaleV3Enabled();
    this.isPointOfSaleV3Enabled = isPointOfSaleV3Enabled;
    this.menuService.navMenuState.isPointOfSaleV3Enabled = isPointOfSaleV3Enabled;
    this.menuItemsMapper();
  }

  private async loadModulesActivationSettings() {
    let response: SystemSetting | undefined;
    if (this.onlineOfflineService.isOnline) {
      response = await this.systemSettingService.getModulesBasedOnPricingPlan();
    }
    else {
      response = await this.systemSettingService.getSystemSettingByNameLocalAsync(SystemSettingsEnum.Global);
    }
    const globalSettings = response ? GlobalSettings.clone(JSON.parse(response.value)) : new GlobalSettings();
    this.modulesActivationSettings = globalSettings.modulesActivation;
    this.renderNavMenu();
  }

  private goToNewSalesOrder() {
    if (this.isPointOfSaleV3Enabled)
      this.router.navigate(['/pos/sales-order-v3/new']);
    else
      this.router.navigate(['/pos/sales-order/new']);
  }

  toggleStarred(data: ModulePage, moduleItem: MenuItem) {
    const starredPage = this.menuService.starredPages.find((starredPage: ModulePage) => starredPage.routerLink === data.routerLink);
    const isStarred = starredPage ? false : true;
    if (starredPage) {
      data = { ...data, ...starredPage, module: moduleItem.module };
    }
    // if(moduleItem?.starredCount === 1)  {
    //   (moduleItem.activeTab = ModuleSideNavTabs.starred)
    //   moduleItem.activeTabDone = true;
    // };

    this.menuService.onToggleStarred(isStarred, data, moduleItem);
  }

  menuItemsMapper() {
    this.menuItems = this.menuService.menuItemsMapper([...this.menuService.menu_items]);
  }

  updateLatestOpendByRouterLink(url: string = this.location.path()) {
    const modulePages: ModulePage[] = childrensMap(this.menuService.menu_items);
    const currentUrlWithoutParams: string = `${url}`.split('?')[0];
    const pageDetails = modulePages.find((el: ModulePage) => el.routerLink === currentUrlWithoutParams);
    if (pageDetails) {
      this.pageMenuDateService.getClickedDate(pageDetails.id);
    }
  }
}
